import { OPS_AUTH, USE_MOCK_AUTH } from "../config/api";
import { cleanGhanaPhone } from "../utils/ghanaPhone";
import { resolveMediaUrl } from "../utils/resolveMediaUrl";
import { apiRequest, unwrapApiEnvelope } from "./apiClient";

/** Status labels for badges / filters. */
export const INSTITUTION_STATUS_LABELS = {
  active: "Active",
  inactive: "Deactivated",
  deleted: "Deleted",
  onboarding: "Onboarding",
};

/**
 * Resolve display status from API fields.
 * - deleted_at set OR status "deleted" → deleted (soft-deleted)
 * - status "active" + deleted_at null → active
 * - status "inactive" + deleted_at null → inactive (shown as Deactivated)
 */
export function resolveInstitutionStatus(raw) {
  if (!raw || typeof raw !== "object") return "inactive";

  const deletedAt = raw.deleted_at ?? raw.deletedAt ?? null;
  const NUMERIC_STATUS = { 0: "onboarding", 1: "active", 2: "inactive", 3: "inactive" };

  let status = "active";
  if (raw.status === true) status = "active";
  else if (raw.status === false) status = "inactive";
  else if (typeof raw.status === "number") status = NUMERIC_STATUS[raw.status] ?? "inactive";
  else if (typeof raw.status === "string" && raw.status.trim()) {
    const value = raw.status.trim().toLowerCase();
    if (/^\d+$/.test(value)) status = NUMERIC_STATUS[value] ?? "inactive";
    else if (value === "suspended") status = "inactive";
    else status = value;
  }

  if (deletedAt || status === "deleted") return "deleted";
  if (status === "active") return "active";
  if (status === "onboarding") return "onboarding";
  return "inactive";
}

/** Route/API key for an institution (numeric `providers.id`). */
export function getInstitutionId(institution) {
  if (!institution) return null;
  const raw = institution.id ?? institution.provider_id ?? institution.numericId;
  return raw != null && String(raw) !== "" ? String(raw) : null;
}

/** @deprecated Use getInstitutionId */
export const getInstitutionSlug = getInstitutionId;

/**
 * Map API institution → FE table / detail shape.
 */
export function normalizeInstitution(raw) {
  if (!raw || typeof raw !== "object") return null;

  const stats = raw.stats || {};
  const deletedAt = raw.deleted_at ?? raw.deletedAt ?? null;
  const status = resolveInstitutionStatus(raw);
  const id = String(raw.id ?? raw.provider_id ?? "");
  const legalName = raw.legal_name ?? raw.name ?? "";
  const businessType = raw.business_type ?? raw.type ?? "";
  const primaryEmail = raw.primary_email ?? raw.contactEmail ?? "";
  const primaryPhone = raw.primary_phone_number ?? raw.contactPhone ?? "";
  const socials = raw.socials && typeof raw.socials === "object" ? raw.socials : {};

  return {
    id,
    numericId: raw.id != null && Number.isFinite(Number(raw.id)) ? Number(raw.id) : null,
    sla_days: raw.sla_days != null ? Number(raw.sla_days) : null,
    contract_sla_days:
      raw.contract_sla_days != null
        ? Number(raw.contract_sla_days)
        : raw.sla_days != null
          ? Number(raw.sla_days)
          : null,
    manager_assignment_days:
      raw.manager_assignment_days != null ? Number(raw.manager_assignment_days) : null,
    agent_engagement_days:
      raw.agent_engagement_days != null ? Number(raw.agent_engagement_days) : null,
    legal_name: legalName,
    name: legalName,
    code: raw.code ?? "",
    business_type: businessType,
    type: businessType,
    region: raw.region ?? "",
    city: raw.city ?? "",
    gps_address: raw.gps_address ?? raw.address ?? "",
    location: raw.location ?? "",
    address: raw.gps_address ?? raw.address ?? "",
    primary_email: primaryEmail,
    primary_phone_number: primaryPhone,
    contactEmail: primaryEmail,
    contactPhone: primaryPhone,
    secondary_email: raw.secondary_email ?? "",
    secondary_phone_number: raw.secondary_phone_number ?? "",
    website: raw.website ?? "",
    logo: resolveMediaUrl(raw.logo ?? null),
    socials: {
      instagram: socials.instagram ?? socials.ig ?? "",
      x: socials.x ?? socials.twitter ?? "",
      facebook: socials.facebook ?? socials.fb ?? "",
      tiktok: socials.tiktok ?? "",
      youtube: socials.youtube ?? "",
      whatsapp: socials.whatsapp ?? "",
    },
    channels: {
      sms: Boolean(raw.channels?.sms),
      whatsapp: Boolean(raw.channels?.whatsapp),
      email: Boolean(raw.channels?.email),
    },
    // Legacy keys kept so older detail/list cells keep rendering
    regNumber: raw.code ?? "",
    primaryContact: legalName,
    activeCases: Number(raw.active_cases ?? stats.active_cases ?? raw.activeCases ?? 0),
    outstanding: Number(raw.outstanding ?? stats.outstanding ?? 0),
    portalUsers: 1,
    status,
    since: raw.created_at ? String(raw.created_at).slice(0, 10) : raw.since ?? null,
    lastActivity: raw.updated_at
      ? String(raw.updated_at).slice(0, 10)
      : raw.lastActivity ?? null,
    caseInstitutionLabel: raw.caseInstitutionLabel || legalName || "",
    email_verified_at: raw.email_verified_at ?? null,
    last_login_at: raw.last_login_at ?? null,
    created_at: raw.created_at ?? null,
    updated_at: raw.updated_at ?? null,
    deleted_at: deletedAt,
    canManage: raw.can_manage === true || raw.can_manage === 1,
  };
}

function extractInstitutionsList(data) {
  if (Array.isArray(data)) {
    return { institutions: data, pagination: null };
  }
  if (!data || typeof data !== "object") {
    return { institutions: [], pagination: null };
  }

  let institutions = [];
  if (Array.isArray(data.institutions)) institutions = data.institutions;
  else if (Array.isArray(data.items)) institutions = data.items;
  else if (Array.isArray(data.data)) institutions = data.data;

  return {
    institutions,
    pagination: data.pagination ?? data.meta ?? null,
  };
}

function extractInstitution(data) {
  if (!data || typeof data !== "object") return null;
  if (data.institution && typeof data.institution === "object") return data.institution;
  if (Array.isArray(data.institutions)) return data.institutions[0] ?? null;
  if (data.data && typeof data.data === "object") return extractInstitution(data.data);
  return data.id != null || data.name || data.legal_name ? data : null;
}

/**
 * Normalize institutions dashboard summary (stat cards only).
 * Expected keys: total_institutions, active_institutions, total_active_cases, total_outstanding
 */
export function normalizeInstitutionsSummary(raw) {
  if (!raw || typeof raw !== "object") {
    return {
      totalInstitutions: 0,
      activeInstitutions: 0,
      totalActiveCases: 0,
      totalOutstanding: 0,
    };
  }

  return {
    totalInstitutions: Number(raw.total_institutions ?? 0),
    activeInstitutions: Number(raw.active_institutions ?? 0),
    totalActiveCases: Number(raw.total_active_cases ?? 0),
    totalOutstanding: Number(raw.total_outstanding ?? 0),
  };
}

/**
 * Build POST /api/ops/institutions body from the onboard form.
 */
export function buildOnboardPayload(form) {
  const socials = form.socials || {};
  const channels = form.channels || {};
  const primaryPhone = cleanGhanaPhone(form.primary_phone_number || form.contactPhone || "");
  const secondaryPhone = cleanGhanaPhone(form.secondary_phone_number || "");

  return {
    legal_name: String(form.legal_name || form.name || "").trim(),
    business_type: form.business_type || form.type || "",
    status: form.status || "active",
    primary_email: String(form.primary_email || form.contactEmail || "").trim().toLowerCase(),
    primary_phone_number: primaryPhone || String(form.primary_phone_number || form.contactPhone || "").trim(),
    region: form.region || "",
    city: String(form.city || "").trim(),
    gps_address: String(form.gps_address || form.address || "").trim(),
    location: String(form.location || "").trim() || null,
    secondary_email: String(form.secondary_email || "").trim().toLowerCase() || null,
    secondary_phone_number: secondaryPhone || null,
    website: String(form.website || "").trim() || null,
    logo: form.logo ? String(form.logo).trim() : null,
    socials: {
      instagram: String(socials.instagram || socials.ig || "").trim() || null,
      x: String(socials.x || socials.twitter || "").trim() || null,
      facebook: String(socials.facebook || socials.fb || "").trim() || null,
      tiktok: String(socials.tiktok || "").trim() || null,
      youtube: String(socials.youtube || "").trim() || null,
    },
    channels: {
      sms: Boolean(channels.sms),
      whatsapp: Boolean(channels.whatsapp),
      email: Boolean(channels.email),
    },
    contract_sla_days: form.contract_sla_days != null && form.contract_sla_days !== ""
      ? Number(form.contract_sla_days)
      : null,
    manager_assignment_days: form.manager_assignment_days != null && form.manager_assignment_days !== ""
      ? Number(form.manager_assignment_days)
      : undefined,
    agent_engagement_days: form.agent_engagement_days != null && form.agent_engagement_days !== ""
      ? Number(form.agent_engagement_days)
      : undefined,
  };
}

let listCache = { key: null, promise: null, result: null };

/**
 * GET /api/ops/institutions
 * Concurrent callers (incl. StrictMode double-mount) share one request.
 */
export async function fetchInstitutions(token, params = {}) {
  if (USE_MOCK_AUTH || !token) {
    return null;
  }

  const query = new URLSearchParams();
  if (params.page) query.set("page", String(params.page));
  // Backend validates `limit` (not per_page).
  const limit = params.limit ?? params.per_page;
  if (limit) query.set("limit", String(limit));
  if (params.search) query.set("search", params.search);
  if (params.status) query.set("status", params.status);

  const qs = query.toString();
  const path = qs ? `${OPS_AUTH.institutions}?${qs}` : OPS_AUTH.institutions;
  const key = `${token}|${path}`;

  if (listCache.key === key && listCache.result) {
    return listCache.result;
  }

  if (listCache.key === key && listCache.promise) {
    return listCache.promise;
  }

  listCache = {
    key,
    result: null,
    promise: (async () => {
      const json = await apiRequest(path, { method: "GET", token });
      const data = unwrapApiEnvelope(json);
      const { institutions, pagination } = extractInstitutionsList(data);
      const result = {
        institutions: institutions.map(normalizeInstitution).filter(Boolean),
        pagination,
      };
      listCache = { key, promise: null, result };
      return result;
    })().catch((err) => {
      if (listCache.key === key) {
        listCache = { key: null, promise: null, result: null };
      }
      throw err;
    }),
  };

  return listCache.promise;
}

let summaryCache = { token: null, promise: null, result: null };

/**
 * GET /api/ops/institutions/summary
 * Dashboard cards only — separate from the institutions list.
 */
export async function fetchInstitutionsSummary(token) {
  if (USE_MOCK_AUTH || !token) {
    return normalizeInstitutionsSummary(null);
  }

  if (summaryCache.token === token && summaryCache.result) {
    return summaryCache.result;
  }

  if (summaryCache.token === token && summaryCache.promise) {
    return summaryCache.promise;
  }

  summaryCache = {
    token,
    result: null,
    promise: (async () => {
      const json = await apiRequest(OPS_AUTH.institutionsSummary, {
        method: "GET",
        token,
      });
      const result = normalizeInstitutionsSummary(unwrapApiEnvelope(json));
      summaryCache = { token, promise: null, result };
      return result;
    })().catch((err) => {
      summaryCache = { token: null, promise: null, result: null };
      throw err;
    }),
  };

  return summaryCache.promise;
}

export function clearInstitutionsCache() {
  listCache = { key: null, promise: null, result: null };
  summaryCache = { token: null, promise: null, result: null };
  detailRequests.clear();
}

/**
 * GET /api/ops/institutions/{provider_id}
 */
const detailRequests = new Map();

export async function fetchInstitution(token, providerId) {
  if (!providerId) throw new Error("Institution id is required.");
  if (USE_MOCK_AUTH || !token) return null;

  const key = `${token}|${providerId}`;
  if (detailRequests.has(key)) return detailRequests.get(key);

  const promise = (async () => {
    const json = await apiRequest(
      `${OPS_AUTH.institutions}/${encodeURIComponent(providerId)}`,
      { method: "GET", token }
    );
    return normalizeInstitution(extractInstitution(unwrapApiEnvelope(json)));
  })();

  detailRequests.set(key, promise);
  try {
    return await promise;
  } finally {
    if (detailRequests.get(key) === promise) detailRequests.delete(key);
  }
}

/**
 * PUT /api/ops/institutions/{provider_slug}
 * `changes` must contain only fields changed by the operator.
 */
export async function updateInstitutionDetails(token, providerId, changes) {
  if (!providerId) throw new Error("Institution id is required.");
  if (!changes || !Object.keys(changes).length) {
    throw new Error("No institution changes to save.");
  }

  const body = { ...changes };
  if (body.primary_phone_number != null) {
    body.primary_phone_number =
      cleanGhanaPhone(body.primary_phone_number) || String(body.primary_phone_number).trim();
  }
  if (body.secondary_phone_number != null) {
    body.secondary_phone_number = cleanGhanaPhone(body.secondary_phone_number) || null;
  }

  if (USE_MOCK_AUTH || !token) return null;

  const json = await apiRequest(
    `${OPS_AUTH.institutions}/${encodeURIComponent(providerId)}`,
    { method: "PUT", token, body }
  );
  return normalizeInstitution(extractInstitution(unwrapApiEnvelope(json)));
}

const STATUS_ACTIONS = new Set(["active", "inactive", "restore"]);

/**
 * POST /api/ops/institutions/{provider_slug}/status
 * Payload: { status: "active" | "inactive" | "restore" }
 */
export async function updateInstitutionStatus(token, providerId, status) {
  if (!providerId) throw new Error("Institution id is required.");

  const nextStatus = String(status || "").trim().toLowerCase();
  if (!STATUS_ACTIONS.has(nextStatus)) {
    throw new Error('Status must be "active", "inactive", or "restore".');
  }

  if (USE_MOCK_AUTH || !token) return null;

  const json = await apiRequest(
    `${OPS_AUTH.institutions}/${encodeURIComponent(providerId)}/status`,
    { method: "POST", token, body: { status: nextStatus } }
  );
  return normalizeInstitution(extractInstitution(unwrapApiEnvelope(json)));
}

/**
 * DELETE /api/ops/institutions/{provider_slug}
 * Soft-deletes the institution (backend sets deleted_at + status deleted).
 */
export async function deleteInstitution(token, providerId) {
  if (!providerId) throw new Error("Institution id is required.");

  if (USE_MOCK_AUTH || !token) return null;

  const json = await apiRequest(
    `${OPS_AUTH.institutions}/${encodeURIComponent(providerId)}`,
    { method: "DELETE", token }
  );
  const data = unwrapApiEnvelope(json);
  const raw = extractInstitution(data);
  const normalized = normalizeInstitution(raw);
  if (normalized) {
    return {
      ...normalized,
      status: "deleted",
      deleted_at: normalized.deleted_at || new Date().toISOString(),
    };
  }
  return {
    id: String(providerId),
    status: "deleted",
    deleted_at: new Date().toISOString(),
  };
}

/**
 * POST /api/ops/institutions
 */
export async function createInstitution(token, form) {
  const body = buildOnboardPayload(form);

  if (!body.legal_name || !body.business_type) {
    throw new Error("Legal name and business type are required.");
  }
  if (!body.primary_email || !body.primary_phone_number) {
    throw new Error("Primary email and phone are required.");
  }

  if (USE_MOCK_AUTH || !token) {
    return normalizeInstitution({
      id: Date.now(),
      code: `MOCK-${Date.now().toString(36).toUpperCase()}`,
      ...body,
      status: "active",
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    });
  }

  const json = await apiRequest(OPS_AUTH.institutions, {
    method: "POST",
    token,
    body,
  });

  const data = unwrapApiEnvelope(json);
  // Response may be the institution directly, or nested under institution / data
  const raw =
    data?.institution ||
    (data?.legal_name || data?.name ? data : null) ||
    data?.data ||
    data;

  return normalizeInstitution(raw) || normalizeInstitution({ ...body, id: Date.now(), status: "active" });
}
